import dayjs from "dayjs";

import { single } from "@/lib/orpc-batch";
import { orpcClient } from "@/lib/orpc-client";

export interface CalendarEvent {
  id: string;
  title: string;
  startDate: string;
  endDate: string;
  allDay: boolean;
  startTime?: string;
  endTime?: string;
  description?: string;
  isCompleted: boolean;
  color?: string;
  style?: Record<string, unknown> | null;
  tagIds: string[];
  createdAt: string;
  updatedAt?: string;
  createdByName?: string | null;
  updatedByName?: string | null;
}

export type CalendarEventInput = Omit<
  CalendarEvent,
  "id" | "createdAt" | "updatedAt" | "createdByName" | "updatedByName"
>;

export interface CalendarSubscription {
  icsUrl: string;
  webcalUrl: string;
  googleUrl: string;
}

export interface ServerCalendarEvent {
  id: string;
  title: string;
  description?: string | null;
  allDay: boolean;
  startAt?: Date | string | null;
  endAt?: Date | string | null;
  startDate?: string | null;
  endDate?: string | null;
  isCompleted?: boolean;
  style?: Record<string, unknown> | null;
  tagIds?: string[];
  createdAt?: Date | string;
  updatedAt?: Date | string;
  createdByName?: string | null;
  updatedByName?: string | null;
}

const DATE_FORMAT = "YYYY-MM-DD";
const TIME_FORMAT = "HH:mm";

function toIso(value?: Date | string | null): string {
  if (!value) return "";
  return value instanceof Date ? value.toISOString() : value;
}

export function fromServerEvent(raw: ServerCalendarEvent): CalendarEvent {
  const style = raw.style ?? null;
  const base = {
    id: raw.id,
    title: raw.title,
    description: raw.description || undefined,
    isCompleted: raw.isCompleted ?? false,
    color: typeof style?.color === "string" && style.color ? style.color : undefined,
    style,
    tagIds: raw.tagIds ?? [],
    createdAt: toIso(raw.createdAt),
    updatedAt: toIso(raw.updatedAt) || undefined,
    createdByName: raw.createdByName ?? null,
    updatedByName: raw.updatedByName ?? null,
  };
  if (raw.allDay) {
    const startDate = raw.startDate ?? "";
    const exclusiveEnd = raw.endDate ?? startDate;
    const endDate = dayjs(exclusiveEnd).subtract(1, "day").format(DATE_FORMAT);
    return {
      ...base,
      allDay: true,
      startDate,
      endDate: endDate < startDate ? startDate : endDate,
    };
  }
  const start = dayjs(raw.startAt ?? undefined);
  const end = dayjs(raw.endAt ?? raw.startAt ?? undefined);
  return {
    ...base,
    allDay: false,
    startDate: start.format(DATE_FORMAT),
    endDate: end.format(DATE_FORMAT),
    startTime: start.format(TIME_FORMAT),
    endTime: end.format(TIME_FORMAT),
  };
}

function buildStyle(input: CalendarEventInput): Record<string, unknown> | null {
  const { color: _previous, ...rest } = input.style ?? {};
  if (input.color) return { ...rest, color: input.color };
  return Object.keys(rest).length > 0 ? rest : null;
}

export function toServerPayload(input: CalendarEventInput) {
  const time = input.allDay
    ? {
        allDay: true,
        startDate: input.startDate,
        endDate: dayjs(input.endDate).add(1, "day").format(DATE_FORMAT),
        startAt: null,
        endAt: null,
      }
    : {
        allDay: false,
        startDate: null,
        endDate: null,
        startAt: dayjs(`${input.startDate} ${input.startTime ?? "00:00"}`).toDate(),
        endAt: dayjs(`${input.endDate} ${input.endTime ?? "00:00"}`).toDate(),
      };
  return {
    title: input.title,
    description: input.description || null,
    isCompleted: input.isCompleted,
    style: buildStyle(input),
    tagIds: input.tagIds,
    ...time,
  };
}

export async function listCalendarEvents(): Promise<CalendarEvent[]> {
  const rows = (await orpcClient.calendarEvent.list({})) as ServerCalendarEvent[];
  return (rows ?? []).map(fromServerEvent);
}

export async function createCalendarEvent(
  input: CalendarEventInput
): Promise<CalendarEvent> {
  const row = await single(
    orpcClient.calendarEvent.create([toServerPayload(input)] as any) as any
  );
  return fromServerEvent(row as ServerCalendarEvent);
}

export async function updateCalendarEvent(
  id: string,
  input: CalendarEventInput
): Promise<CalendarEvent> {
  const row = await single(
    orpcClient.calendarEvent.update([{ id, ...toServerPayload(input) }] as any) as any
  );
  return fromServerEvent(row as ServerCalendarEvent);
}

export async function deleteCalendarEvent(id: string): Promise<void> {
  await single(orpcClient.calendarEvent.delete([{ id }]) as any);
}

export async function fetchCalendarSubscription(): Promise<CalendarSubscription> {
  return (await orpcClient.calendarEvent.subscription()) as CalendarSubscription;
}

export function isEventVisibleForTags(
  event: CalendarEvent,
  filterTagIds: string[]
): boolean {
  if (filterTagIds.length === 0) return true;
  if (event.tagIds.length === 0) return true;
  return event.tagIds.some((id) => filterTagIds.includes(id));
}

export const MAX_EVENT_SPAN_DAYS = 366;

export function eventDates(event: CalendarEvent): string[] {
  const dates: string[] = [];
  const end = new Date(`${event.endDate}T00:00:00Z`).getTime();
  let cursor = new Date(`${event.startDate}T00:00:00Z`).getTime();
  while (cursor <= end && dates.length < MAX_EVENT_SPAN_DAYS) {
    dates.push(new Date(cursor).toISOString().slice(0, 10));
    cursor += 86400000;
  }
  return dates;
}

export function isMultiDay(event: CalendarEvent): boolean {
  return event.endDate > event.startDate;
}
